import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Paper,
  IconButton,
  InputAdornment,
  Alert,
  Divider,
  CircularProgress,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Email, 
  Security,
  Login as LoginIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, resetPassword } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  
  // Password reset dialog state
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setErrors({});
    
    try {
      await login(formData.email, formData.password);
      setSuccessMessage('Login successful! Welcome back!');
      
      // Redirect to dashboard after successful login
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
      
    } catch (error) {
      console.error('Login error:', error);
      
      if (error.code === 'auth/user-not-found') {
        setErrors({ email: 'No account found with this email' });
      } else if (error.code === 'auth/wrong-password') {
        setErrors({ password: 'Incorrect password' });
      } else if (error.code === 'auth/invalid-email') {
        setErrors({ email: 'Invalid email address' });
      } else if (error.code === 'auth/too-many-requests') {
        setErrors({ general: 'Too many failed attempts. Please try again later.' });
      } else {
        setErrors({ general: 'Login failed. Please check your credentials.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!resetEmail.trim()) {
      setErrors({ resetEmail: 'Email is required' });
      return;
    }
    
    if (!/\S+@\S+\.\S+/.test(resetEmail)) {
      setErrors({ resetEmail: 'Please enter a valid email' });
      return;
    }
    
    setResetLoading(true);
    setErrors({});
    
    try {
      await resetPassword(resetEmail);
      setResetMessage('Password reset email sent! Check your inbox.');
      
      setTimeout(() => {
        setResetDialogOpen(false);
        setResetEmail('');
        setResetMessage('');
      }, 3000);
      
    } catch (error) {
      console.error('Password reset error:', error);
      
      if (error.code === 'auth/user-not-found') {
        setErrors({ resetEmail: 'No account found with this email' });
      } else {
        setErrors({ resetEmail: 'Failed to send reset email. Please try again.' });
      }
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <Box sx={{ 
      pt: 12, 
      pb: 8, 
      minHeight: '100vh', 
      backgroundImage: 'url(/BearsBulls.jpeg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      position: 'relative'
    }}>
      {/* Gradient Overlay for readability */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(245,247,250,0.7) 0%, rgba(195,207,226,0.7) 100%)',
          zIndex: 1,
        }}
      />
      
      {/* Content */}
      <Box sx={{ position: 'relative', zIndex: 2 }}>
        <Container maxWidth="sm">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Box textAlign="center" mb={4}>
              <Typography variant="h2" gutterBottom sx={{ 
                background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 'bold'
              }}>
                Welcome Back
              </Typography>
              <Typography variant="h5" color="text.secondary" mb={2}>
                Sign in to your FX Wars account
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Continue your trading journey and compete for massive prizes!
              </Typography>
            </Box>
          </motion.div>

          {/* Success/Error Messages */}
          {successMessage && (
            <Fade in={!!successMessage}>
              <Alert severity="success" sx={{ mb: 3 }}>
                {successMessage}
              </Alert>
            </Fade>
          )}
          
          {errors.general && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.general}
            </Alert>
          )}

          {/* Login Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Paper elevation={4} sx={{ borderRadius: 3, overflow: 'hidden' }}>
              {/* Header */}
              <Box sx={{ 
                p: 3, 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                textAlign: 'center'
              }}>
                <LoginIcon sx={{ fontSize: 48, color: 'white', mb: 1 }} />
                <Typography variant="h5" color="white" fontWeight="bold">
                  Sign In
                </Typography>
              </Box>
              
              {/* Form Content */}
              <Box sx={{ p: 4 }}>
                <form onSubmit={handleSubmit}>
                  <Box sx={{ mb: 3 }}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      variant="outlined"
                      value={formData.email}
                      onChange={handleInputChange('email')}
                      error={!!errors.email}
                      helperText={errors.email}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                  
                  <Box sx={{ mb: 3 }}>
                    <TextField
                      fullWidth
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      variant="outlined"
                      value={formData.password}
                      onChange={handleInputChange('password')}
                      error={!!errors.password}
                      helperText={errors.password}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Security color="primary" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                  
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{
                      background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                      py: 1.5,
                      mb: 2
                    }}
                  >
                    {loading ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                        Signing In...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </form>
                
                <Box textAlign="center" mb={3}>
                  <Button 
                    variant="text" 
                    color="primary"
                    onClick={() => setResetDialogOpen(true)}
                  >
                    Forgot your password?
                  </Button>
                </Box>
                
                <Divider sx={{ my: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    OR
                  </Typography>
                </Divider>
                
                <Box textAlign="center">
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    Don't have an account yet?
                  </Typography>
                  <Button
                    component={Link}
                    to="/register"
                    variant="outlined"
                    fullWidth
                    size="large"
                    sx={{ py: 1.5 }}
                  >
                    Create Account
                  </Button>
                </Box>
              </Box>
            </Paper>
          </motion.div>
        </Container>

        {/* Password Reset Dialog */}
        <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Typography variant="h5" fontWeight="bold">
              Reset Password
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Enter your email address and we'll send you a link to reset your password.
            </Typography>
            
            {resetMessage && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {resetMessage}
              </Alert>
            )}
            
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              variant="outlined"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              error={!!errors.resetEmail}
              helperText={errors.resetEmail}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="primary" />
                  </InputAdornment>
                ),
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setResetDialogOpen(false)} variant="outlined">
              Cancel
            </Button>
            <Button 
              onClick={handlePasswordReset}
              variant="contained"
              disabled={resetLoading}
              sx={{
                background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)'
              }}
            >
              {resetLoading ? (
                <>
                  <CircularProgress size={18} sx={{ mr: 1, color: 'white' }} />
                  Sending...
                </>
              ) : (
                'Send Reset Email'
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default LoginPage; 